import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
    FEATURE_FLAG_DEFAULTS,
    featureFlagChain,
    type FeatureFlagKey,
    type FeatureFlagMap,
} from '~~/layers/base/constants/featureFlag'
import { isDeviceOffline } from '~~/layers/base/utils/isDeviceOffline'
import { useFeatureFlagService } from '@/services/featureFlag.service'

type FetchStatus = 'idle' | 'loading' | 'ready' | 'error'

/** Options for {@link useFeatureFlagsStore.refresh}. */
export type RefreshFeatureFlagsOptions = {
    /**
     * Treat a failed re-resolve as "unknown", not "keep what we had" — see the identity-change
     * discussion on `refresh()`.
     */
    failClosed?: boolean
    /** Give up waiting after this long and settle per `failClosed`. Never rejects. */
    timeoutMs?: number
}

/**
 * Default ceiling on an identity-change refresh. Chosen against the two things it sits between:
 * `useApiFetch` retries 3x with **zero** delay, so an unreachable endpoint would otherwise run
 * four back-to-back attempts with no bound; and `useScreenLock` auto-unlocks at 10s, so the wait
 * has to finish comfortably inside the overlay that is covering it.
 */
const DEFAULT_IDENTITY_REFRESH_TIMEOUT_MS = 5_000

/**
 * Client-side cache for `GET /api/feature-flags` (Cloudflare Flagship — see
 * `SPEC_feature_flags_TK-12413.md`). Fetched once per session, then read synchronously by
 * `useFeatureFlag()` so UI checks cost nothing.
 *
 * Kept in sync by `plugins/feature-flags-sync.client.ts` (login/logout) and, on native Capacitor,
 * `apps/capacitor/plugins/feature-flags-resume.client.ts` (app foreground). The Capacitor cold-start
 * splash additionally waits on the first resolve — see `useSessionBootstrapSplash` (TK-13203).
 *
 * Deliberately **not** persisted to disk/Preferences: flag state is the thing a kill-switch needs
 * to change quickly, and a persisted copy would widen the staleness window.
 */
export const useFeatureFlagsStore = defineStore('featureFlags', () => {
    const { doFetchFeatureFlags } = useFeatureFlagService()

    /**
     * What a flag reads as before the first successful fetch, and after a fetch that failed with
     * nothing ever loaded: `false` for every key, matching the server's `FEATURE_FLAG_FALLBACK`.
     *
     * The two answer the same question about two different unreachable things — our Worker here,
     * Flagship behind it there — so they resolve identically and neither needs a per-key or
     * per-platform case. This used to be platform-aware, mirroring an `ecommerce` fail-open
     * exception that no longer exists.
     */
    function failClosedDefaults(): FeatureFlagMap {
        return { ...FEATURE_FLAG_DEFAULTS }
    }

    /** Resolved flags. Starts at the fail-closed defaults so reads are always safe. */
    const flags = ref<FeatureFlagMap>(failClosedDefaults())
    const status = ref<FetchStatus>('idle')

    /**
     * True once a fetch has succeeded at least once. Drives the stale-while-error policy: a later
     * failure keeps the last-known map rather than reverting to all-false, so a flaky network
     * doesn't make a live feature vanish mid-session.
     */
    const hasLoadedOnce = ref(false)

    /** Coalesces concurrent `fetchFlags` calls (route middleware + plugin + nav all at once). */
    let fetchFlagsInFlight: Promise<void> | null = null

    /**
     * Which identity the current/last fetch was started under. Bumped by an identity-change
     * `refresh()` (login / logout).
     *
     * Exists because three separate triggers can race for the same fetch — the `isLoggedIn` watcher
     * in `feature-flags-sync.client.ts`, the awaited call in the auth flows, and the Capacitor
     * foreground-resume plugin. Plain promise coalescing is not enough on its own: a fetch started
     * *before* an identity swap resolves against the old session cookie, so a caller that needs
     * post-swap values must not be allowed to adopt it, and its late answer must not overwrite the
     * newer map. Callers at the same generation still share one request.
     */
    let identityGeneration = 0

    /** Generation `fetchFlagsInFlight` belongs to; only meaningful while that promise is set. */
    let inFlightGeneration = -1

    /**
     * Whether the in-flight fetch fails closed. Latches on: if *any* caller sharing the request
     * asked for it, the shared result must honour the stricter policy — the looser caller reading
     * a stale cross-identity map is exactly what this prevents.
     */
    let inFlightFailClosed = false

    /** Resets to fail-closed defaults. Used on teardown and on a failed identity-change refresh. */
    function clearFlags(): void {
        flags.value = failClosedDefaults()
        status.value = 'idle'
        hasLoadedOnce.value = false
    }

    /**
     * Loads `GET /api/feature-flags` and replaces the cached map.
     *
     * On error: keeps the previous map when one was ever loaded (status `error`, values intact);
     * falls back to fail-closed defaults when nothing has ever loaded, or when the request was
     * tagged `failClosed` because it followed an identity change.
     */
    async function fetchFlags(): Promise<void> {
        // Same generation → genuinely the same question, so share the answer. A newer generation
        // must start its own request; the older one is left to settle and be discarded below.
        if (fetchFlagsInFlight && inFlightGeneration === identityGeneration) {
            await fetchFlagsInFlight
            return
        }

        const generation = identityGeneration
        inFlightGeneration = generation

        const request = (async () => {
            // Skip the network when the OS/store already says offline — fail-closed defaults are the
            // same answer as a transport error, without WebKit "Failed to load resource" console noise.
            // Lives inside the coalesced request so concurrent callers still share one check/fetch.
            if (await isDeviceOffline()) {
                if (generation !== identityGeneration) {
                    return
                }
                if (!hasLoadedOnce.value || inFlightFailClosed) {
                    flags.value = failClosedDefaults()
                    hasLoadedOnce.value = false
                }
                status.value = 'error'
                return
            }

            status.value = 'loading'
            const { data, error: err } = await doFetchFeatureFlags()

            // Identity changed while this was in flight: the answer describes someone else. Drop
            // it — the newer fetch owns the store's state now.
            if (generation !== identityGeneration) {
                return
            }

            if (err.value || !data.value?.flags) {
                if (!hasLoadedOnce.value || inFlightFailClosed) {
                    flags.value = failClosedDefaults()
                    hasLoadedOnce.value = false
                }
                status.value = 'error'
                return
            }

            // Spread over defaults so a key the server doesn't know about is still present.
            // Server values win outright; the fail-closed base only fills a key the server does
            // not know about (older Worker, newly added key), which then reads `false`.
            flags.value = { ...failClosedDefaults(), ...data.value.flags }
            hasLoadedOnce.value = true
            status.value = 'ready'
        })()

        fetchFlagsInFlight = request

        try {
            await request
        } finally {
            // Only clear if we are still the current request — a newer generation may have
            // replaced it while we were awaiting.
            if (fetchFlagsInFlight === request) {
                fetchFlagsInFlight = null
                inFlightFailClosed = false
            }
        }
    }

    /**
     * Returns the cached map when ready, otherwise fetches (deduped). Use from route middleware so
     * a gated route never renders before flags are known.
     */
    async function ensureFlagsLoaded(): Promise<FeatureFlagMap> {
        if (status.value === 'ready') {
            return flags.value
        }
        await fetchFlags()
        return flags.value
    }

    /**
     * Forces a re-resolve. Resets `status` first so a concurrent `ensureFlagsLoaded()` awaits the
     * fresh values instead of reading the stale pre-login map (FE-5).
     *
     * Pass `failClosed` when the caller is reacting to an **identity change** (login / logout).
     * Two things change:
     *
     * - The generation is bumped, so this cannot adopt a request that was already in flight
     *   against the previous session cookie.
     * - A failure resets to the platform-aware defaults instead of keeping the last-known map.
     *   The ordinary stale-while-error policy is right *within* one identity — a flaky network
     *   shouldn't make a live feature vanish — but wrong across identities: keeping the map would
     *   leave a signed-out user holding the previous account's per-user targeted flags.
     *
     * Never rejects: auth flows await this while holding a screen lock, so it must always hand
     * control back for the `unlock()`.
     */
    async function refresh(options: RefreshFeatureFlagsOptions = {}): Promise<void> {
        const { failClosed = false, timeoutMs = DEFAULT_IDENTITY_REFRESH_TIMEOUT_MS } = options

        if (failClosed) {
            // Join an identity refresh that is already running rather than starting another.
            // The sync plugin's watcher and the auth flow's awaited call both fire off the *same*
            // `isLoggedIn` flip, so a second bump would not only duplicate the request — it would
            // strand the first caller, whose result is then discarded as stale, letting it unlock
            // before the winning fetch landed. Only a fetch that predates this identity (not
            // fail-closed, or an older generation) has to be superseded.
            const joinableIdentityFetch =
                fetchFlagsInFlight !== null &&
                inFlightFailClosed &&
                inFlightGeneration === identityGeneration

            if (!joinableIdentityFetch) {
                identityGeneration += 1
            }
            inFlightFailClosed = true
        }

        status.value = 'loading'

        let timer: ReturnType<typeof setTimeout> | undefined
        const timedOut = await Promise.race([
            // `doFetchFeatureFlags` reports transport failures through its `error` ref rather than
            // throwing, but a synchronous blow-up inside the fetch layer would still land here and
            // must not escape into an auth flow's overlay.
            fetchFlags().then(
                () => false,
                () => true
            ),
            new Promise<boolean>((resolve) => {
                timer = setTimeout(() => resolve(true), timeoutMs)
            }),
        ])
        clearTimeout(timer)

        if (!timedOut) {
            return
        }

        // The request is still running and may yet answer for this generation — that late result is
        // welcome and will be applied. Meanwhile the caller has to render *something*, so settle on
        // the safe value rather than leaving a cross-identity map on screen.
        if (failClosed) {
            clearFlags()
        }
        status.value = 'error'
    }

    /**
     * Synchronous read, resolving the flag hierarchy: a child key is only enabled when its whole
     * ancestor chain is (see `FEATURE_FLAG_PARENTS`). Callers pass the narrowest key they care
     * about and never need to check the parent themselves.
     *
     * `false` still means "off or not yet known" (FE-6), but UI callers no longer have to
     * distinguish the two: nothing renders before flags are known (TK-13203). Only a caller that
     * renders something on the *negative* branch — an upsell, an "unavailable" state — needs to
     * check `status` / `hasLoadedOnce` as well, since "not yet known" would show the wrong branch.
     */
    function isEnabled(key: FeatureFlagKey): boolean {
        // Every flag in the chain must be on, so a parent works as a real kill-switch: `contacts`
        // off hides `contacts-recent` regardless of its own value. The hierarchy lives in
        // FEATURE_FLAG_PARENTS, so no caller has to remember to check the parent itself.
        return featureFlagChain(key).every((flagKey) => flags.value[flagKey] === true)
    }

    return {
        flags,
        status,
        hasLoadedOnce,
        fetchFlags,
        refresh,
        ensureFlagsLoaded,
        isEnabled,
        clearFlags,
    }
})
